import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle, DownloadSimple, FileArrowUp, FileText, Gavel,
  PaperPlaneTilt, SealCheck, X,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const inputClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const errorMessage = (error, fallback) => {
  const detail = error.response?.data?.detail;
  return typeof detail === "string" ? detail : detail?.message || fallback;
};

export default function LegalDocumentActions({
  document,
  opportunityId,
  onUpdated,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const versionInput = useRef(null);
  const signedInput = useRef(null);
  const [showReview, setShowReview] = useState(false);
  const [showSigned, setShowSigned] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [review, setReview] = useState({
    decision: "approved",
    reviewer_name: "",
    reviewer_capacity: "",
    reviewer_registration: "",
    notes: "",
    confirm_review: false,
  });
  const [signed, setSigned] = useState({
    signature_method: "qualified_electronic",
    signed_at: "",
    signature_notes: "",
  });

  const { data: approvedTemplates = [], isLoading: templatesLoading } = useQuery({
    queryKey: [
      "approved-legal-templates",
      document.document_type,
      document.language,
    ],
    queryFn: async () =>
      (
        await api.get("/deal-legal/templates", {
          params: {
            document_type: document.document_type,
            language: document.language,
            status: "approved",
          },
        })
      ).data,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: ["legal-documents", opportunityId],
    });
    onUpdated?.();
  };

  const effectiveTemplateId =
    selectedTemplate || approvedTemplates[0]?.id || "";

  const generateMutation = useMutation({
    mutationFn: async (format) =>
      (
        await api.post(
          `/deal-legal/documents/${document.id}/generate`,
          {
            template_id: effectiveTemplateId,
            format,
          }
        )
      ).data,
    onSuccess: async (file) => {
      await refresh();
      toast.success(
        t(
          "legalDocuments.actions.generated",
          `${String(file.content_type || "").includes("pdf") ? "PDF" : "DOCX"} generated successfully`
        )
      );
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      ),
  });

  const versionMutation = useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      return (
        await api.post(
          `/deal-legal/documents/${document.id}/versions`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      ).data;
    },
    onSuccess: async () => {
      await refresh();
      toast.success(
        t("legalDocuments.actions.versionUploaded", "Document version uploaded")
      );
      if (versionInput.current) versionInput.current.value = "";
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      ),
  });

  const submitReviewMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(
          `/deal-legal/documents/${document.id}/submit-review`
        )
      ).data,
    onSuccess: async () => {
      await refresh();
      toast.success(
        t(
          "legalDocuments.actions.submittedForReview",
          "Submitted for legal review"
        )
      );
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      ),
  });

  const legalReviewMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post(
          `/deal-legal/documents/${document.id}/legal-review`,
          review
        )
      ).data,
    onSuccess: async () => {
      await refresh();
      setShowReview(false);
      toast.success(
        review.decision === "approved"
          ? t("legalDocuments.actions.reviewApproved", "Legal review approved")
          : t(
              "legalDocuments.actions.changesRequested",
              "Changes requested"
            )
      );
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      ),
  });

  const signedMutation = useMutation({
    mutationFn: async (file) => {
      const form = new FormData();
      form.append("file", file);
      form.append("signature_method", signed.signature_method);
      form.append("signed_at", signed.signed_at);
      form.append("signature_notes", signed.signature_notes);
      return (
        await api.post(
          `/deal-legal/${document.id}/signed-original`,
          form,
          { headers: { "Content-Type": "multipart/form-data" } }
        )
      ).data;
    },
    onSuccess: async () => {
      await refresh();
      setShowSigned(false);
      toast.success(
        t(
          "legalDocuments.actions.signedOriginalStored",
          "Signed original stored and locked"
        )
      );
      if (signedInput.current) signedInput.current.value = "";
    },
    onError: (error) =>
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      ),
  });

  const download = async () => {
    if (!document.current_file_id) return;
    try {
      const response = await api.get(
        `/files/${document.current_file_id}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(response.data);
      const link = window.document.createElement("a");
      link.href = url;
      link.download =
        document.versions?.at(-1)?.filename ||
        `${document.title || "legal-document"}.pdf`;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        errorMessage(
          error,
          t("legalDocuments.actionFailed", "Action failed")
        )
      );
    }
  };

  const canUploadVersion = ![
    "signed",
    "superseded",
    "void",
  ].includes(document.status);
  const canSubmitReview =
    Boolean(document.current_file_id) &&
    ["draft", "changes_required"].includes(document.status);
  const canReview =
    user?.role === "admin" &&
    document.status === "pending_legal_review";
  const canUploadSigned =
    user?.role === "admin" &&
    document.legal_review_status === "approved" &&
    ["approved", "sent", "partially_signed"].includes(document.status) &&
    !document.signed_file_id;
  const canGenerate = ["draft", "changes_required"].includes(
    document.status
  );

  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      {canGenerate && (
        <div className="mb-4 border border-[#C5A059]/30 bg-[#C5A059]/[0.06] p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#8A692D]">
            <FileText size={16} />
            {t(
              "legalDocuments.actions.controlledGeneration",
              "Controlled document generation"
            )}
          </div>
          {templatesLoading ? (
            <p className="text-xs text-slate-500">
              {t("legalDocuments.loading", "Loading…")}
            </p>
          ) : approvedTemplates.length === 0 ? (
            <p className="text-xs leading-5 text-amber-700">
              {t(
                "legalDocuments.actions.noApprovedTemplate",
                "No approved template matches this document type and language."
              )}
            </p>
          ) : (
            <div className="space-y-2">
              <select
                value={effectiveTemplateId}
                onChange={(event) => setSelectedTemplate(event.target.value)}
                className="h-9 w-full border border-slate-200 bg-white px-2 text-xs outline-none focus:border-[#C5A059]"
              >
                {approvedTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.title} — v{template.template_version}
                  </option>
                ))}
              </select>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  disabled={generateMutation.isPending}
                  onClick={() => generateMutation.mutate("pdf")}
                  className="h-9 rounded-none bg-[#0B2545] text-xs hover:bg-[#12345e]"
                >
                  {t("legalDocuments.actions.generatePdf", "Generate PDF")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={generateMutation.isPending}
                  onClick={() => generateMutation.mutate("docx")}
                  className="h-9 rounded-none text-xs"
                >
                  {t("legalDocuments.actions.generateDocx", "Generate DOCX")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {document.current_file_id && (
          <Button
            type="button"
            variant="outline"
            onClick={download}
            className="h-9 rounded-none text-xs"
          >
            <DownloadSimple size={15} className="mr-2" />
            {t("legalDocuments.actions.download", "Download")}
          </Button>
        )}

        {canUploadVersion && (
          <>
            <input
              ref={versionInput}
              type="file"
              accept=".pdf,.docx"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) versionMutation.mutate(file);
              }}
            />
            <Button
              type="button"
              variant="outline"
              disabled={versionMutation.isPending}
              onClick={() => versionInput.current?.click()}
              className="h-9 rounded-none text-xs"
            >
              <FileArrowUp size={15} className="mr-2" />
              {t("legalDocuments.actions.uploadVersion", "Upload version")}
            </Button>
          </>
        )}

        {canSubmitReview && (
          <Button
            type="button"
            disabled={submitReviewMutation.isPending}
            onClick={() => submitReviewMutation.mutate()}
            className="h-9 rounded-none bg-[#0B2545] text-xs hover:bg-[#12345e]"
          >
            <PaperPlaneTilt size={15} className="mr-2" />
            {t("legalDocuments.actions.submitReview", "Submit for review")}
          </Button>
        )}

        {canReview && (
          <Button
            type="button"
            onClick={() => setShowReview(true)}
            className="h-9 rounded-none bg-[#C5A059] text-xs text-white hover:bg-[#A77E35]"
          >
            <Gavel size={15} className="mr-2" />
            {t("legalDocuments.actions.legalReview", "Legal review")}
          </Button>
        )}

        {canUploadSigned && (
          <Button
            type="button"
            onClick={() => setShowSigned(true)}
            className="h-9 rounded-none bg-emerald-700 text-xs hover:bg-emerald-800"
          >
            <SealCheck size={15} className="mr-2" />
            {t(
              "legalDocuments.actions.recordSignedOriginal",
              "Record signed original"
            )}
          </Button>
        )}
      </div>

      {showReview && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-950/70 p-4">
          <div className="mx-auto mt-8 max-w-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-[#0B2545] px-6 py-5 text-white">
              <h3 className="font-serif text-xl">
                {t("legalDocuments.actions.legalReview", "Legal review")}
              </h3>
              <button type="button" onClick={() => setShowReview(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <select
                value={review.decision}
                onChange={(event) =>
                  setReview({ ...review, decision: event.target.value })
                }
                className={inputClass}
              >
                <option value="approved">
                  {t("legalDocuments.options.approved", "Approved")}
                </option>
                <option value="rejected">
                  {t("legalDocuments.options.rejected", "Rejected")}
                </option>
              </select>
              <Input
                value={review.reviewer_name}
                onChange={(event) =>
                  setReview({ ...review, reviewer_name: event.target.value })
                }
                placeholder={t(
                  "legalDocuments.actions.reviewerName",
                  "Qualified reviewer name"
                )}
                className="h-11 rounded-none"
              />
              <Input
                value={review.reviewer_capacity}
                onChange={(event) =>
                  setReview({
                    ...review,
                    reviewer_capacity: event.target.value,
                  })
                }
                placeholder={t(
                  "legalDocuments.actions.reviewerCapacity",
                  "Professional capacity"
                )}
                className="h-11 rounded-none"
              />
              <Input
                value={review.reviewer_registration}
                onChange={(event) =>
                  setReview({
                    ...review,
                    reviewer_registration: event.target.value,
                  })
                }
                placeholder={t(
                  "legalDocuments.actions.reviewerRegistration",
                  "Professional registration reference"
                )}
                className="h-11 rounded-none"
              />
              <textarea
                value={review.notes}
                onChange={(event) =>
                  setReview({ ...review, notes: event.target.value })
                }
                placeholder={t(
                  "legalDocuments.actions.reviewNotes",
                  "Legal review notes"
                )}
                rows={4}
                className={`${inputClass} h-auto py-3`}
              />
              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={review.confirm_review}
                  onChange={(event) =>
                    setReview({
                      ...review,
                      confirm_review: event.target.checked,
                    })
                  }
                  className="mt-1"
                />
                {t(
                  "legalDocuments.actions.confirmReview",
                  "I confirm that the identified qualified professional reviewed this exact document version."
                )}
              </label>
              <Button
                type="button"
                disabled={legalReviewMutation.isPending}
                onClick={() => legalReviewMutation.mutate()}
                className="w-full rounded-none bg-[#0B2545] hover:bg-[#12345e]"
              >
                <CheckCircle size={17} className="mr-2" />
                {t("legalDocuments.actions.recordDecision", "Record decision")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {showSigned && (
        <div className="fixed inset-0 z-[70] overflow-y-auto bg-slate-950/70 p-4">
          <div className="mx-auto mt-8 max-w-xl bg-white shadow-2xl">
            <div className="flex items-center justify-between bg-[#0B2545] px-6 py-5 text-white">
              <h3 className="font-serif text-xl">
                {t(
                  "legalDocuments.actions.recordSignedOriginal",
                  "Record signed original"
                )}
              </h3>
              <button type="button" onClick={() => setShowSigned(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <select
                value={signed.signature_method}
                onChange={(event) =>
                  setSigned({
                    ...signed,
                    signature_method: event.target.value,
                  })
                }
                className={inputClass}
              >
                {[
                  "wet_ink",
                  "electronic",
                  "qualified_electronic",
                  "mixed",
                ].map((method) => (
                  <option key={method} value={method}>
                    {t(
                      `legalDocuments.options.${method}`,
                      method.replaceAll("_", " ")
                    )}
                  </option>
                ))}
              </select>
              <Input
                type="datetime-local"
                value={signed.signed_at}
                onChange={(event) =>
                  setSigned({ ...signed, signed_at: event.target.value })
                }
                className="h-11 rounded-none"
              />
              <textarea
                value={signed.signature_notes}
                onChange={(event) =>
                  setSigned({
                    ...signed,
                    signature_notes: event.target.value,
                  })
                }
                placeholder={t(
                  "legalDocuments.actions.signatureNotes",
                  "Signature notes"
                )}
                rows={3}
                className={`${inputClass} h-auto py-3`}
              />
              <input
                ref={signedInput}
                type="file"
                accept="application/pdf,.pdf"
                className={inputClass}
              />
              <p className="text-xs text-slate-500">
                {t(
                  "legalDocuments.actions.signedWarning",
                  "The signed PDF will be stored as an immutable original and cannot be replaced."
                )}
              </p>
              <Button
                type="button"
                disabled={signedMutation.isPending}
                onClick={() => {
                  const file = signedInput.current?.files?.[0];
                  if (!file) {
                    toast.error(
                      t(
                        "legalDocuments.actions.selectSignedPdf",
                        "Select the signed PDF first"
                      )
                    );
                    return;
                  }
                  signedMutation.mutate(file);
                }}
                className="w-full rounded-none bg-emerald-700 hover:bg-emerald-800"
              >
                <SealCheck size={17} className="mr-2" />
                {t(
                  "legalDocuments.actions.storeAndLock",
                  "Store and lock signed original"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
